import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/CrudModule";
import { StatusBadge } from "@/components/StatusBadge";
import { SophiaSchedulesPanel } from "@/components/SophiaSchedulesPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { exportToCsv, fmtDateTime } from "@/lib/format";
import { Loader2, Download, RefreshCw, X, Play } from "lucide-react";

const EXPORT_PAGE_SIZE = 200; // matches the server's max allowed `limit` for /voice-ai/auto/sessions
const EXPORT_MAX_PAGES = 100; // safety guard: up to 20,000 sessions in one export

const STATUS_OPTIONS = [
  ["", "Any status"],
  ["auto_dialing_gate,auto_dialing,auto_active,auto_transferring,dialing,active", "Active"],
  ["completed", "Completed"],
  ["call_failed,failed", "Failed"],
  ["human_takeover", "Human Takeover"],
  ["awaiting_review", "Awaiting Review"],
  ["planned", "Planned"],
];
const LAUNCH_MODE_OPTIONS = [["", "Any launch"], ["manual_exact_id", "Manual"], ["manual_selected", "Selected Cohort"], ["scheduled", "Scheduled"]];
const TEST_MODE_OPTIONS = [["", "Test + Real"], ["false", "Real calls only"], ["true", "Test mode only"]];

const SESSION_EXPORT_COLUMNS = [
  { key: "id", label: "Session ID" },
  { key: "created_at", label: "Created" },
  { key: "client_name", label: "Client" },
  { key: "carrier", label: "Carrier" },
  { key: "policy_number", label: "Policy #" },
  { key: "status", label: "Status" },
  { key: "launch_mode", label: "Launch Mode" },
  { key: "test_mode", label: "Test Mode" },
  { key: "provisional_disposition", label: "AI-Reported Disposition" },
  { key: "recording_status", label: "Recording Status" },
  { key: "followup_id", label: "Follow-Up ID" },
];

function StatCard({ label, value, tone = "" }) {
  return (
    <div className="rounded-lg border bg-card p-4" data-testid={`sophia-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

function SessionDetail({ session, onClose }) {
  const [recUrl, setRecUrl] = useState(null);
  const [recError, setRecError] = useState("");
  const [recLoading, setRecLoading] = useState(false);

  useEffect(() => {
    return () => { if (recUrl) URL.revokeObjectURL(recUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playRecording = async () => {
    setRecError(""); setRecLoading(true);
    try {
      const res = await api.get(`/voice-ai/auto/sessions/${session.id}/recording`, { responseType: "blob" });
      const blob = res.data;
      const ct = (res.headers?.["content-type"] || blob?.type || "").toLowerCase();
      const bad = !blob || blob.size === 0 || ct.includes("json") || ct.includes("text/");
      if (bad) {
        let msg = "Recording is not available.";
        try {
          if (blob && (ct.includes("json") || ct.includes("text/"))) {
            const txt = await blob.text();
            try { msg = JSON.parse(txt).detail || msg; } catch { if (txt) msg = txt; }
          }
        } catch { /* noop */ }
        setRecError(msg); toast.error(msg);
        return;
      }
      if (recUrl) URL.revokeObjectURL(recUrl);
      setRecUrl(URL.createObjectURL(blob));
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Recording could not be loaded.";
      setRecError(msg); toast.error(msg);
    } finally { setRecLoading(false); }
  };

  const Row = ({ label, value }) => (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm">{value || value === 0 ? value : <span className="text-muted-foreground">—</span>}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="sophia-session-detail">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-background p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Session {session.id.slice(0, 8)}…</h2>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="sophia-session-detail-close"><X className="h-4 w-4" /></Button>
        </div>

        <div className="grid grid-cols-2 gap-3 rounded-lg border bg-secondary/30 p-4">
          <Row label="Client" value={session.client_name} />
          <Row label="Phone" value={session.phone || session.to_number} />
          <Row label="Status" value={<StatusBadge value={session.status} />} />
          <Row label="Launch Mode" value={session.launch_mode} />
          <Row label="Test Mode" value={session.test_mode ? "Yes — TEST MODE" : "No — real call"} />
          <Row label="Created" value={fmtDateTime(session.created_at)} />
          <Row label="Completed" value={fmtDateTime(session.completed_at)} />
          <Row label="Launched By" value={session.launched_by_name || session.owner} />
        </div>

        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-900">
            Carrier-Verified Facts (at dial time)
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <Row label="Carrier" value={session.carrier} />
            <Row label="Policy #" value={session.policy_number} />
            <Row label="Effective Date" value={session.effective_date} />
            <Row label="Verified Issue" value={session.verified_issue} />
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50/60 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-violet-900">
            AI-Reported — Not Independently Verified
          </div>
          <p className="mt-1 text-[11px] text-violet-900/70">
            Sophia's own account of the call. Treat as a lead, not a confirmed fact — cross-check against the
            carrier-verified fields above before acting on it.
          </p>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <Row label="Disposition" value={session.provisional_disposition} />
            <Row label="Callback Requested" value={session.callback_at} />
          </div>
          {session.safe_summary && (
            <div className="mt-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Safe Summary</div>
              <div className="mt-0.5 text-sm">{session.safe_summary}</div>
            </div>
          )}
        </div>

        {session.status === "human_takeover" && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-900">Human Takeover</div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <Row label="Taken Over By" value={session.takeover_by_name} />
              <Row label="Taken Over At" value={fmtDateTime(session.takeover_at)} />
            </div>
          </div>
        )}

        <div className="mt-4 rounded-lg border p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recording</div>
          {session.recording_status === "ready" ? (
            <div className="mt-2">
              {!recUrl && (
                <Button size="sm" variant="outline" onClick={playRecording} disabled={recLoading} data-testid="sophia-play-recording">
                  {recLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}
                  Load & Play Recording
                </Button>
              )}
              {recError && <p className="mt-2 text-xs text-destructive" data-testid="sophia-recording-error">{recError}</p>}
              {recUrl && (
                <audio controls preload="auto" src={recUrl} className="mt-2 w-full" data-testid="sophia-recording-audio"
                  onError={() => setRecError("Playback failed in the browser for this recording.")} />
              )}
            </div>
          ) : (
            <div className="mt-1 text-sm text-muted-foreground" data-testid="sophia-recording-unavailable">
              {session.recording_status === "processing" ? "Recording is still processing." :
               session.recording_status === "failed" ? "Recording failed and is unavailable." :
               "No recording available for this session."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SophiaOps() {
  const { user } = useAuth();
  const isManager = ["admin", "ops_manager"].includes(user?.role);
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", launch_mode: "", test_mode: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const loadSummary = () => {
    if (!isManager) return;
    api.get("/voice-ai/auto/dashboard-summary").then(({ data }) => setSummary(data)).catch(() => {});
  };

  const buildFilterParams = () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.launch_mode) params.launch_mode = filters.launch_mode;
    if (filters.test_mode) params.test_mode = filters.test_mode;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return params;
  };

  const loadSessions = () => {
    setLoading(true);
    const params = { ...buildFilterParams(), limit: 100 };
    api.get("/voice-ai/auto/sessions", { params })
      .then(({ data }) => { setSessions(data.sessions || []); setTotal(data.count || 0); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load Sophia sessions"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadSummary(); }, [isManager, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadSessions(); }, [filters, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => setRefreshKey((x) => x + 1);

  const openSession = async (row) => {
    try {
      const { data } = await api.get(`/voice-ai/auto/sessions/${row.id}/detail`);
      setSelected(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load session detail");
    }
  };

  const exportSessions = async () => {
    setExporting(true);
    try {
      const filterParams = buildFilterParams();
      const all = [];
      let offset = 0;
      for (let page = 0; page < EXPORT_MAX_PAGES; page++) {
        const { data } = await api.get("/voice-ai/auto/sessions", {
          params: { ...filterParams, limit: EXPORT_PAGE_SIZE, offset },
        });
        const rows = data.sessions || [];
        all.push(...rows);
        offset += rows.length;
        if (rows.length < EXPORT_PAGE_SIZE || all.length >= (data.count || 0)) break;
      }
      if (!all.length) { toast.info("No sessions to export for the current filters"); return; }
      exportToCsv("sophia-sessions.csv", all, SESSION_EXPORT_COLUMNS);
      toast.success(`Exported ${all.length} session(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => setFilters({ status: "", launch_mode: "", test_mode: "", date_from: "", date_to: "" });

  return (
    <div className="space-y-5" data-testid="sophia-ops">
      <PageHeader title="Sophia Operations" subtitle="Live call status, scheduled batches, results, recordings, and exports — all from real, persisted session data.">
        <Button variant="outline" size="sm" onClick={refresh} data-testid="sophia-refresh">
          <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={exportSessions} disabled={exporting} data-testid="sophia-export">
          {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
          {exporting ? "Exporting…" : `Export All Sessions (${total})`}
        </Button>
      </PageHeader>

      {isManager && summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="sophia-summary-cards">
          <StatCard label="Active" value={summary.counts_by_status?.active ?? 0} tone="text-[#315A7D]" />
          <StatCard label="Completed" value={summary.counts_by_status?.completed ?? 0} tone="text-emerald-700" />
          <StatCard label="Failed" value={summary.counts_by_status?.failed ?? 0} tone="text-rose-700" />
          <StatCard label="Human Takeover" value={summary.counts_by_status?.human_takeover ?? 0} tone="text-amber-700" />
          <StatCard label="Today (real)" value={summary.today_real ?? 0} />
          <StatCard label="Today (test)" value={summary.today_test_mode ?? 0} />
        </div>
      )}
      {isManager && summary?.emergency_paused && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700" data-testid="sophia-paused-banner">
          Sophia calling is PAUSED for this agency — no new calls will dial. Manage this from Agency Admin → Sophia Controls.
        </div>
      )}

      <div className="rounded-lg border bg-card p-4">
        <div className="mb-3 font-medium">Sessions</div>
        <div className="mb-3 flex flex-wrap items-end gap-2" data-testid="sophia-filters">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
            <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "__any" ? "" : v }))}>
              <SelectTrigger className="h-9 w-44" data-testid="sophia-filter-status"><SelectValue placeholder="Any status" /></SelectTrigger>
              <SelectContent>{STATUS_OPTIONS.map(([v, l]) => <SelectItem key={l} value={v || "__any"}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Launch</label>
            <Select value={filters.launch_mode} onValueChange={(v) => setFilters((f) => ({ ...f, launch_mode: v === "__any" ? "" : v }))}>
              <SelectTrigger className="h-9 w-40" data-testid="sophia-filter-launch-mode"><SelectValue placeholder="Any launch" /></SelectTrigger>
              <SelectContent>{LAUNCH_MODE_OPTIONS.map(([v, l]) => <SelectItem key={l} value={v || "__any"}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Mode</label>
            <Select value={filters.test_mode} onValueChange={(v) => setFilters((f) => ({ ...f, test_mode: v === "__any" ? "" : v }))}>
              <SelectTrigger className="h-9 w-40" data-testid="sophia-filter-test-mode"><SelectValue placeholder="Test + Real" /></SelectTrigger>
              <SelectContent>{TEST_MODE_OPTIONS.map(([v, l]) => <SelectItem key={l} value={v || "__any"}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">From</label>
            <Input type="date" className="h-9 w-36" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="sophia-filter-date-from" />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">To</label>
            <Input type="date" className="h-9 w-36" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="sophia-filter-date-to" />
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="sophia-filter-clear">Clear</Button>
        </div>

        {loading ? (
          <div className="grid min-h-32 place-items-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2">Created</th><th>Client</th><th>Carrier / Policy</th><th>Status</th>
                <th>Launch</th><th>Mode</th><th>AI Disposition</th><th>Recording</th>
              </tr></thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="cursor-pointer border-b text-xs hover:bg-secondary/30"
                    onClick={() => openSession(s)} data-testid={`sophia-session-row-${s.id}`}>
                    <td className="py-2">{fmtDateTime(s.created_at)}</td>
                    <td>{s.client_name || "—"}</td>
                    <td>{s.carrier || "—"} {s.policy_number ? `· ${s.policy_number}` : ""}</td>
                    <td><StatusBadge value={s.status} /></td>
                    <td>{s.launch_mode || "—"}</td>
                    <td>{s.test_mode ? <span className="font-semibold text-amber-700">TEST</span> : "Real"}</td>
                    <td>{s.provisional_disposition || "—"}</td>
                    <td>{s.recording_status === "ready" ? "Ready" : (s.recording_status || "—")}</td>
                  </tr>
                ))}
                {sessions.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">No Sophia sessions match these filters.</td></tr>
                )}
              </tbody>
            </table>
            <div className="mt-2 text-right text-[11px] text-muted-foreground">{sessions.length} of {total} shown</div>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card p-4">
        <SophiaSchedulesPanel refreshKey={refreshKey} />
      </div>

      {selected && <SessionDetail session={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
